import { useState } from 'react'
import { styled } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { RichTextEditor } from './RichTextEditor'
import { intl } from '../../../../../i18n'
import { useDiscussionActions } from './hooks/useDiscussionActions'
import { colorPalette } from '../../../../style/colorPalette'
import {
  useDiscussionActionContext,
  useDiscussionStateContext,
} from '../../context/DiscussionContext'

type NewThreadFormProps = {
  projectUuid: string
  versionPageUuid: string
  discussionUuid?: string
  onClose: () => void
  onRefresh?: () => void
}

export const NewThreadForm = ({
  projectUuid,
  versionPageUuid,
  discussionUuid,
  onClose,
  onRefresh,
}: NewThreadFormProps) => {
  const { pendingTarget } = useDiscussionStateContext()
  const { clearPendingTarget } = useDiscussionActionContext()
  const [isTargetExpanded, setIsTargetExpanded] = useState(false)
  const { handleCreateThread } = useDiscussionActions({
    versionPageUuid,
    discussionUuid,
    onClose,
    onRefresh,
  })

  const handleSubmit = async (content: string) => {
    const targetUuids = pendingTarget ? [pendingTarget.uuid] : undefined
    await handleCreateThread(content, targetUuids)
  }

  return (
    <FormContainer>
      <FormTitle>
        {intl.formatMessage({ id: 'discussion.new.title' })}
      </FormTitle>
      {pendingTarget && (
        <TargetRow>
          <TargetLabel>
            {intl.formatMessage({ id: 'discussion.new.target' })}
          </TargetLabel>
          <TargetCluster>
            <TargetChip>
              <TargetChipName isExpanded={isTargetExpanded}>
                {pendingTarget.name}
              </TargetChipName>
              <TargetChipIconButton
                onClick={() => setIsTargetExpanded(prev => !prev)}
                aria-label={intl.formatMessage({
                  id: isTargetExpanded
                    ? 'discussion.new.target.collapse'
                    : 'discussion.new.target.expand',
                })}
              >
                {isTargetExpanded ? (
                  <ExpandLessIcon fontSize="inherit" />
                ) : (
                  <ExpandMoreIcon fontSize="inherit" />
                )}
              </TargetChipIconButton>
              <TargetChipIconButton
                onClick={clearPendingTarget}
                aria-label={intl.formatMessage({
                  id: 'discussion.new.target.remove',
                })}
              >
                <CloseIcon fontSize="inherit" />
              </TargetChipIconButton>
            </TargetChip>
          </TargetCluster>
        </TargetRow>
      )}
      <RichTextEditor
        placeholder={intl.formatMessage({ id: 'discussion.new.placeholder' })}
        onSubmit={handleSubmit}
        onCancel={onClose}
        showCancel={true}
        submitLabel={intl.formatMessage({ id: 'discussion.new.submit' })}
        projectUuid={projectUuid}
      />
    </FormContainer>
  )
}

const FormContainer = styled('div')({
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  padding: '16px',
  backgroundColor: '#ffffff',
  marginBottom: '16px',
})

const FormTitle = styled('h4')({
  margin: '0 0 12px 0',
  fontSize: '14px',
  fontWeight: 600,
})

const TargetRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
})

const TargetLabel = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[5],
  flexShrink: 0,
})

const TargetCluster = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  minWidth: 0,
  flex: 1,
})

const TargetChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  minHeight: '22px',
  padding: '2px 4px 2px 10px',
  borderRadius: '11px',
  backgroundColor: colorPalette.skyBlue[1],
  color: colorPalette.skyBlue[10],
  fontSize: '12px',
  fontWeight: 600,
  maxWidth: '100%',
})

const TargetChipName = styled('span', {
  shouldForwardProp: prop => prop !== 'isExpanded',
})<{ isExpanded: boolean }>(({ isExpanded }) => ({
  ...(isExpanded
    ? {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }
    : {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '180px',
      }),
}))

const TargetChipIconButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '16px',
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: '14px',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[2],
  },
})
